import { buttonStyle } from 'components/buttons/buttonStyle';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import { Button } from 'components/ui/button';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useGetTenantConfigurationQuery, useUpdateDefaultCurrencyMutation } from 'store/api/tenants';
import { SelectType } from 'types/forms';
import { getClassifierOptionByCode, getValidOptionsFromClassifier } from 'utils/formUtil';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useSettingSave } from './useSettingSave';

interface DefaultCurrencyForm {
    currency: SelectType<string, string> | null;
}

/** The DEFAULT_CURRENCY row's value: the firm's currency, saved apart from the switch. An empty field removes it. */
export function DefaultCurrencyControl() {
    const { data: tenantConfiguration } = useGetTenantConfigurationQuery();
    const { classifiers } = useClassifiers({ codes: ['CURRENCY'] });
    const [updateDefaultCurrency] = useUpdateDefaultCurrencyMutation();
    const save = useSettingSave();
    // Until the stored value and the options are in (still loading, or the CURRENCY read failed), the field reads
    // empty and a Save would clear the default.
    const disabled =
        !useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']) || !classifiers['CURRENCY'] || !tenantConfiguration;

    const options = useMemo(
        () => getValidOptionsFromClassifier<string, string>(classifiers['CURRENCY']),
        [classifiers]
    );
    // The stored code is looked up among every currency, a retired one included, so a Save never drops it unseen.
    const { control, setValue, handleSubmit } = useForm<DefaultCurrencyForm>({
        values: {
            currency: getClassifierOptionByCode(
                classifiers['CURRENCY'],
                tenantConfiguration?.defaultCurrencyTypeCode ?? null
            ),
        },
    });

    return (
        <>
            <SelectInput<DefaultCurrencyForm, SelectType<string, string>>
                control={control}
                setValue={setValue}
                name="currency"
                label="back-office-settings.DEFAULT_CURRENCY.value-label"
                placeholder="back-office-settings.DEFAULT_CURRENCY.value-placeholder"
                options={options}
                getOptionLabel="label"
                getOptionIdentifier="value"
                clearable
                disabled={disabled}
                wrapperClassName="w-[280px]"
            />
            <Button
                {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'h-9')}
                disabled={disabled}
                onClick={() =>
                    void handleSubmit(({ currency }) =>
                        save(() => updateDefaultCurrency({ defaultCurrencyTypeCode: currency?.value ?? null }).unwrap())
                    )()
                }
            >
                <FormattedMessage id="save" />
            </Button>
        </>
    );
}
